import {
  Box,
  Divider,
  GKButton,
  Grid2,
  LockIcon,
  PopConfirm,
  Show,
  Tab,
  Tabs,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import NoteModal from "Pages/AdviceRegisterNotes/NoteModal";
import React, { lazy, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { CLIENT_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";
import ClientCard from "./ClientCard";

const ProfilePage = lazy(() => import("./ProfilePages/EditClientDetails"));
const HoldingPage = lazy(() => import("./SumaryPages/Holdings"));
const PortfolioPage = lazy(() => import("./SumaryPages/Portfolios"));
const FamilyPage = lazy(() => import("./SumaryPages/Family"));
const ComplianceTab = lazy(() => import("./ComplianceTabs"));
const ClientNotes = lazy(() => import("./ClientNotes"));
const ClientPermissions = lazy(
  () => import("./SettingsPages/ClientPermissions"),
);
const AccessPage = lazy(() => import("./SettingsPages/Access"));
// const SettingTab = lazy(() => import("./SettingsPages/SettingTab"));

const ClientDetailsPage = () => {
  useDocumentTitle("Client Details");

  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state?.user?.userData);
  const { clientERMPageTabs } = useAppSelector((state) => state?.app?.tab);

  const tabs = [
    { canAccess: true, label: "Profile", component: ProfilePage },
    { canAccess: true, label: "Holdings", component: HoldingPage },
    { canAccess: true, label: "Portfolios", component: PortfolioPage },
    { canAccess: true, label: "Family", component: FamilyPage },
    {
      canAccess: userData?.grantAccess,
      label: "Onboarding",
      component: ComplianceTab,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Notes",
      component: ClientNotes,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Permissions",
      component: ClientPermissions,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Access",
      component: AccessPage,
    },
    // {
    //   canAccess: userData?.grantAccess,
    //   label: "Settings",
    //   component: SettingTab,
    // },
  ];

  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: any) => axiosInstance.delete(`/client/${data}/`),
    mutationKey: ["DELETE_CLIENT"],
    onSuccess: () => {
      navigate(-1);
    },
  });

  const { mutate: sendRegistrationLink, isLoading: linkLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post("/send-client-reg/", { client: data }),
    mutationKey: ["SEND_REGISTRATION_LINK"],
  });

  const { data: clientData, isFetching } = useFetchQuery({
    key: [QueryKeys.CLIENTS_LIST, id],
    route: CLIENT_BY_ID(id),
  });

  return (
    <DashboardLayout title="Client Details" loading={isFetching}>
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <ClientCard clientData={clientData?.data} />
        </Grid2>
        <Grid2 container size={12} display={"inline"}>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Tabs
              variant="scrollable"
              scrollButtons={false}
              value={clientERMPageTabs}
              onChange={(e, val) => {
                dispatch(TAB({ tabKey: "clientERMPageTabs", tabValue: val }));
              }}
            >
              {tabs.map((tabItem, index) => (
                <Tab
                  key={index}
                  disabled={!tabItem.canAccess}
                  label={
                    <Box display={"flex"} gap={1}>
                      <Typography>{tabItem.label}</Typography>
                      {!tabItem.canAccess && (
                        <LockIcon width={16} height={16} />
                      )}
                    </Box>
                  }
                  disableRipple
                />
              ))}
            </Tabs>

            {clientERMPageTabs === 7 && (
              <div style={{ gap: "8px", display: "flex" }}>
                <GKButton
                  loading={linkLoading}
                  variant="outlined"
                  onClick={() => sendRegistrationLink(id)}
                >
                  Send Registration Link
                </GKButton>
                <GKButton
                  loading={isLoading}
                  variant="outlined"
                  color="error"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  Delete Client
                </GKButton>
              </div>
            )}

            {clientERMPageTabs === 5 && (
              <GKButton
                variant="contained"
                onClick={() => setOpenNoteModal(true)}
              >
                Add Note
              </GKButton>
            )}
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          {tabs.map((tab, index) => (
            <Show.When key={index} isTrue={clientERMPageTabs === index}>
              {React.createElement(tab.component)}
            </Show.When>
          ))}
        </Grid2>
      </Grid2>

      <PopConfirm
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        setOpen={setAnchorEl}
        onSuccess={() => mutate(id)}
      />

      <Show.When isTrue={openNoteModal}>
        <NoteModal
          open={openNoteModal}
          setOpen={setOpenNoteModal}
          noteDetails={{}}
          clientId={id}
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default ClientDetailsPage;
